import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../../shared/core/discriminated-union-read.schema";
import { ImportableVibeCardProjectionSchema } from "./card-binding-enums.schemas";
import { VibeAppCardBindingSchema } from "./card-binding-read.schemas";
import {
  ListVibeAppEnvVarsResponseSchema,
  VibeAppEnvVarSchema,
  VibeEnvVarNameSchema,
  VibeEnvVarScopeSchema
} from "./env-vars.schemas";
import { VibeAppSecretRefSchema } from "./secret-refs.schemas";

export * from "./card-binding-enums.schemas";
export * from "./card-binding-read.schemas";
export * from "./grantable-access-cards.schemas";

// ----------------------------------------------------------------------------
// List env vars — the card-aware response
// ----------------------------------------------------------------------------

/**
 * `GET /api/vibe/apps/:appId/env`, extended with the cards imported into the
 * same namespace.
 *
 * ## Why the cards ride HERE and not only on the console's merged read
 *
 * The console's `GetAppEnvironment` carries no `@AllowOrgApiKey`, so
 * `CompositeAuthGuard` refuses every API-key request to it — deliberately,
 * because importing a card delegates a human's credential authority. But the
 * CLI holds an API key and nothing else, and `nexus vibe env list` must still
 * show what a deployment will actually see. Omitting cards here would let the
 * CLI print a complete-looking environment that is missing entries.
 *
 * ## Why the field is OPTIONAL, and why that is not laziness
 *
 * The CLI ships standalone to npm and is routinely pointed at a backend older
 * than itself. ABSENT means "this server has nothing to say about cards";
 * `[]` means "it does, and this app has none". Collapsing the two would let an
 * old backend assert, through a new CLI, that an app holds no cards — which is
 * a claim it is not in a position to make.
 *
 * `.extend` rather than a redefinition: `envVars` stays byte-identically where
 * every existing consumer already reads it, and this is purely additive.
 */
export const ListVibeAppEnvVarsWithCardsResponseSchema = ListVibeAppEnvVarsResponseSchema.extend({
  cardBindings: z.array(VibeAppCardBindingSchema).optional()
});

// ----------------------------------------------------------------------------
// The merged environment read (console)
// ----------------------------------------------------------------------------

/**
 * One row of the app's environment, whatever backs it.
 *
 * A discriminated union on `kind` rather than three parallel arrays. The three
 * kinds share ONE namespace at the database level — `VibeAppEnvKey` makes a
 * collision between them unrepresentable — so serving them as one sorted list
 * is the shape that matches the constraint. Three arrays would invite the
 * client to invent its own merge order, and a client-side merge order is
 * exactly the shadowing bug the namespace was introduced to delete.
 *
 * No branch carries a secret. A literal's `value` is plaintext configuration
 * the tenant already owns, a Vault pointer carries a path, and a card carries
 * a handle.
 */
export const VibeEnvEntrySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("LITERAL"), envVar: VibeAppEnvVarSchema }),
  z.object({ kind: z.literal("VAULT_SECRET"), secretRef: VibeAppSecretRefSchema }),
  z.object({ kind: z.literal("CARD"), binding: VibeAppCardBindingSchema })
]);

export type VibeEnvEntryDto = z.infer<typeof VibeEnvEntrySchema>;

/**
 * An entry as a READER parses it: a kind a newer backend added reads as the
 * server's own word instead of failing the environment read — shown, never
 * dropped, so the list a reader renders stays complete.
 */
export const VibeEnvEntryReadSchema = z.union([
  VibeEnvEntrySchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(VibeEnvEntrySchema) })
]);

export const GetVibeAppEnvironmentPathVarsSchema = z.object({
  appId: z.string().uuid()
});

/**
 * Sorted by (scope, name) SERVER-SIDE, in the order the deployer resolves:
 * ALL first, then the environment-specific scope overwriting by name. The
 * console renders the array as given.
 */
export const GetVibeAppEnvironmentResponseSchema = z.object({
  entries: z.array(VibeEnvEntryReadSchema)
});

// ----------------------------------------------------------------------------
// Create binding (import a card)
// ----------------------------------------------------------------------------

export const CreateVibeAppCardBindingPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const CreateVibeAppCardBindingBodySchema = z.object({
  cardId: z.string().uuid(),
  /**
   * SCREAMING_SNAKE_CASE, the same grammar every other env entry uses. NOT the
   * card's own variable grammar, which is lowercase snake_case — the two live
   * in different name spaces and conflating them produces names that pass one
   * validator and fail the other.
   */
  name: VibeEnvVarNameSchema,
  scope: VibeEnvVarScopeSchema.optional(),
  projection: ImportableVibeCardProjectionSchema,
  /**
   * THE OWNER'S REVOCATION GUARANTEE, in seconds. 0 — the default — means
   * "when I revoke this, it stops NOW".
   *
   * A projection whose worst-case revocation window exceeds this value is
   * REFUSED, never silently downgraded to a weaker delivery path: a guarantee
   * that quietly weakens is worse than one that was never offered. `HANDLE`
   * satisfies every window, so nothing in today's projection set can violate
   * it — the field exists so the refusal has something to read the day a
   * projection that caches a token arrives.
   */
  maxRevocationWindowSeconds: z.number().int().min(0).default(0)
});

export const CreateVibeAppCardBindingResponseSchema = z.object({
  binding: VibeAppCardBindingSchema,
  /**
   * True when the grant landed `PENDING_APPROVAL` rather than `ACTIVE` — the
   * caller held EDITOR on the credential where separation of duty demands an
   * OWNER. The binding exists and authorizes nothing until an approver acts.
   */
  requiresApproval: z.boolean()
});

// ----------------------------------------------------------------------------
// Update binding
// ----------------------------------------------------------------------------

export const UpdateVibeAppCardBindingPathVarsSchema = z.object({
  appId: z.string().uuid(),
  bindingId: z.string().uuid()
});

/**
 * Name and scope only.
 *
 * `projection` is deliberately absent rather than optional-and-refused: a
 * field the schema accepts and the handler rejects reads, to anyone writing a
 * client, as a capability that is temporarily broken. Changing how a card is
 * DELIVERED is a different authorization question from renaming the variable
 * it arrives in, and it re-enters as its own route when a second projection
 * ships.
 */
export const UpdateVibeAppCardBindingBodySchema = z
  .object({
    name: VibeEnvVarNameSchema.optional(),
    scope: VibeEnvVarScopeSchema.optional()
  })
  .refine(
    (body) => body.name !== undefined || body.scope !== undefined,
    "at least one of name or scope must be provided"
  );

export const UpdateVibeAppCardBindingResponseSchema = z.object({
  binding: VibeAppCardBindingSchema
});

// ----------------------------------------------------------------------------
// Delete binding
// ----------------------------------------------------------------------------

export const DeleteVibeAppCardBindingPathVarsSchema = z.object({
  appId: z.string().uuid(),
  bindingId: z.string().uuid()
});

export const DeleteVibeAppCardBindingResponseSchema = z.object({
  deletedId: z.string().uuid()
});
